import { Actions, createEffect, ofType } from '@ngrx/effects';
import { inject } from '@angular/core';
import { SopsActions } from '../state/sops/sops.actions';
import { map, of, switchMap, tap } from 'rxjs';
import { SopsService } from '../../services/sops.service';
import { catchError } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { MsgBoxType, ToastService } from '@ui';

const sopEntityLabel = (translateService: TranslateService): string =>
  translateService.instant('HOME.DETAILS_TABS.SOP') as string;

export const startLoadOrganizationSops = createEffect(
  (
    actions$ = inject(Actions),
    sopsService = inject(SopsService),
  ) =>
    actions$.pipe(
      ofType(SopsActions.startLoadOrganizationSops),
      switchMap(({ orgId }) => {
        return sopsService.getSopsByOrganization(orgId).pipe(
          map((sops) =>
            SopsActions.loadOrganizationSopsSuccess({ sops }),
          ),
          catchError(() =>
            of(SopsActions.loadOrganizationSopsFailed())
          )
        )
      })
    ),
  { functional: true }
);

export const startLoadAccountSops = createEffect(
  (
    actions$ = inject(Actions),
    sopsService = inject(SopsService),
  ) =>
    actions$.pipe(
      ofType(SopsActions.startLoadAccountSops),
      switchMap(({ accountId }) => {
        return sopsService.getSopsByAccount(accountId).pipe(
          map((sops) =>
            SopsActions.loadAccountSopsSuccess({ sops }),
          ),
          catchError(() =>
            of(SopsActions.loadAccountSopsFailed())
          )
        )
      })
    ),
  { functional: true }
);

export const startCreateSop = createEffect(
  (
    actions$ = inject(Actions),
    sopsService = inject(SopsService),
    toastService = inject(ToastService),
    translateService = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(SopsActions.startCreateSop),
      switchMap(({ orgId, createData }) => {
        return sopsService.createSop(orgId, createData).pipe(
          tap(() => {
            toastService.showCustomAlert(
              translateService.instant('TOASTS.ENTITY_CREATED_SUCCESS', {
                entity: sopEntityLabel(translateService),
              }) as string,
              MsgBoxType.SUCCESS,
            );
          }),
          map((sop) =>
            SopsActions.createSopSuccess({ sop }),
          ),
          catchError(() => {
            toastService.showCustomAlert(
              translateService.instant('TOASTS.ENTITY_CREATED_FAIL', {
                entity: sopEntityLabel(translateService),
              }) as string,
              MsgBoxType.ERROR,
            );
            return of(SopsActions.createSopFailed());
          })
        )
      })
    ),
  { functional: true }
);

export const startUpdateSop = createEffect(
  (
    actions$ = inject(Actions),
    sopsService = inject(SopsService),
    toastService = inject(ToastService),
    translateService = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(SopsActions.startUpdateSop),
      switchMap(({ orgId, sopId, updateData }) => {
        return sopsService.updateSop(orgId, sopId, updateData).pipe(
          tap(() => {
            toastService.showCustomAlert(
              translateService.instant('TOASTS.ENTITY_UPDATE_SUCCESS', {
                entity: sopEntityLabel(translateService),
              }) as string,
              MsgBoxType.SUCCESS,
            );
          }),
          map((sop) =>
            SopsActions.updateSopSuccess({ sop }),
          ),
          catchError(() => {
            toastService.showCustomAlert(
              translateService.instant('TOASTS.ENTITY_UPDATE_FAILED', {
                entity: sopEntityLabel(translateService),
              }) as string,
              MsgBoxType.ERROR,
            );
            return of(SopsActions.updateSopFailed());
          })
        )
      })
    ),
  { functional: true }
);

export const startDeleteSop = createEffect(
  (
    actions$ = inject(Actions),
    sopsService = inject(SopsService),
    toastService = inject(ToastService),
    translateService = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(SopsActions.startDeleteSop),
      switchMap(({ orgId, sopId }) => {
        return sopsService.deleteSop(orgId, sopId).pipe(
          tap(() => {
            toastService.showCustomAlert(
              translateService.instant('TOASTS.ENTITY_HAS_BEEN_REMOVED', {
                entity: sopEntityLabel(translateService),
              }) as string,
              MsgBoxType.SUCCESS,
            );
          }),
          map(() =>
            SopsActions.deleteSopSuccess({ sopId }),
          ),
          catchError(() => {
            toastService.showCustomAlert(
              translateService.instant('TOASTS.ENTITY_HAS_BEEN_REMOVED_FAILED', {
                entity: sopEntityLabel(translateService),
              }) as string,
              MsgBoxType.ERROR,
            );
            return of(SopsActions.deleteSopFailed());
          })
        )
      })
    ),
  { functional: true }
);

export const startDuplicateSop = createEffect(
  (
    actions$ = inject(Actions),
    sopsService = inject(SopsService),
    toastService = inject(ToastService),
    translateService = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(SopsActions.startDuplicateSop),
      switchMap(({ orgId, sopId }) => {
        return sopsService.duplicateSop(orgId, sopId).pipe(
          tap(() => {
            toastService.showCustomAlert(
              translateService.instant('TOASTS.ENTITY_CREATED_SUCCESS', {
                entity: sopEntityLabel(translateService),
              }) as string,
              MsgBoxType.SUCCESS,
            );
          }),
          map((sop) => SopsActions.duplicateSopSuccess({ sop })),
          catchError(() => {
            toastService.showCustomAlert(
              translateService.instant('TOASTS.ENTITY_CREATED_FAIL', {
                entity: sopEntityLabel(translateService),
              }) as string,
              MsgBoxType.ERROR,
            );
            return of(SopsActions.duplicateSopFailed());
          }),
        );
      }),
    ),
  { functional: true },
);
